import {
	setLoadingScaps,
	setScapsData,
	setSelectedScap,
} from "@store/modules/creditLimitManagement/scapSlice";
import axios from "axios";
import { clearUser } from "../../store/auth/userSlice"; // Assuming you have this action to clear user data

const handleUnauthorized = (dispatch, navigate) => {
	dispatch(clearUser());
	navigate("/login"); // Redirect to login page
};

export const fetchScaps = async (
	token,
	dispatch,
	navigate,
	page,
	perPage,
	searchTerms,
	paginate = 1,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
		params: {
			page: paginate === 1 ? page : 1,
			per_page: paginate === 1 ? perPage : 10,
			name: searchTerms?.name || null,
			cycle: searchTerms?.cycle || null,
			client: searchTerms?.client || null,
			paginate,
		},
	};

	try {
		dispatch(setLoadingScaps(true));

		const response = await axios.get(`${import.meta.env.VITE_API_BACKEND}api/scap/configs`, config);
		let data;

		if (!response.data.data || response.data.data.length === 0) {
			data = {
				data: [],
				totalItems: 0,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		} else if (paginate === 1) {
			const totalItems = response?.data.total;
			data = {
				data: response.data.data,
				totalItems: totalItems,
				page: paginate === 1 ? page : 1,
				perPage: paginate === 1 ? perPage : 10,
			};
		} else {
			data = {
				data: response.data.data,
				totalItems: 0,
				page: 1,
				perPage: 10,
			};
		}
		dispatch(setScapsData(data));

		return data;
	} catch (error) {
		console.error("Error fetching Scaps data:", error);
		if (error.response && error.response.status === 401) {
			handleUnauthorized(dispatch, navigate);
		}
	} finally {
		dispatch(setLoadingScaps(false));
	}
};

export const fetchScapById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.get(
			`${import.meta.env.VITE_API_BACKEND}api/scap/configs/${id}`,
			config,
		);
		return response.data.data;
	} catch (error) {
		if (error.response && error.response.status === 401) {
			console.error("Unauthorized error, token might be invalid:", error.response);
			handleUnauthorized(dispatch, navigate);
		} else {
			console.error("Error fetching scap data:", error);
		}
	}
};

export const fetchScapDetails = async (id, token, dispatch, navigate) => {
	await dispatch(setSelectedScap(id));

	try {
		const data = await fetchScapById(id, token, dispatch, navigate);
		dispatch(setSelectedScap(data));
	} catch (error) {
		console.error("Error fetching data:", error);
	} finally {
		dispatch(setLoadingScaps(false));
	}
};

export const createScap = async (formData, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "multipart/form-data",
		},
	};

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/scap/configs`,
			formData,
			config,
		);
		return { status: response.status, data: response.data };
	} catch (error) {
		if (error.response) {
			const status = error.response.status;

			if (status === 401) {
				handleUnauthorized(dispatch, navigate);
			} else if (status === 422) {
				return { errors: error.response.data.errors };
			} else if (status === 400) {
				return {
					errors: error.response.data.errors || {
						Error: ["Bad Request - Please check your inputs."],
					},
				};
			}
		}

		console.error("Error creating scap:", error);
		return null;
	}
};

export const deleteScapById = async (id, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	};
	try {
		const response = await axios.delete(
			`${import.meta.env.VITE_API_BACKEND}api/scap/configs/${id}`,
			config,
		);
		if (response.status === 200 || response.status === 202 || response.status === 204) {
			return {
				status: "success",
				message: "Scap deleted successfully.",
			};
		} else {
			return {
				status: "error",
				message: response.data?.message || "An unexpected error occurred.",
			};
		}
	} catch (error) {
		if (error.response) {
			switch (error.response.status) {
				case 401:
					handleUnauthorized(dispatch, navigate);
					break;
				case 404:
					return { status: "not_found", message: "Not found." };
				case 405:
					return {
						status: "method_not_allowed",
						message: "Method Not Allowed",
					};
				case 400:
					return {
						status: "error",
						message: error.response.data?.message || "An error occurred.",
					};
				default:
					return { status: "error", message: "An error occurred." };
			}
		} else {
			return { status: "error", message: "Network error" };
		}
	}
};

export const updateScap = async (id, data, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	try {
		const response = await axios.put(
			`${import.meta.env.VITE_API_BACKEND}api/scap/configs/${id}`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			const status = error.response.status;

			if (status === 401) {
				handleUnauthorized(dispatch, navigate);
			} else if (status === 422) {
				return { errors: error.response.data.errors };
			} else if (status === 400) {
				return {
					errors: error.response.data.errors || {
						Error: ["Bad Request - Please check your inputs."],
					},
				};
			}
		}

		console.error("Error creating scap:", error);
		return null;
	}
};

// scap extension for norway
export const updateScapExtensionNorway = async (id, data, idExt, token, dispatch, navigate) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};
	try {
		const response = await axios.put(
			`${import.meta.env.VITE_API_BACKEND}api/scap/configs/${id}/norway_extension/${idExt}`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			const status = error.response.status;

			if (status === 401) {
				handleUnauthorized(dispatch, navigate);
			} else if (status === 422) {
				return { errors: error.response.data.errors };
			} else if (status === 400) {
				return {
					errors: error.response.data.errors || {
						Error: ["Bad Request - Please check your inputs."],
					},
				};
			}
		}

		console.error("Error creating scap:", error);
		return null;
	}
};

// scap extension for norway
export const createScapExtensionNorway = async (
	id, // scapId
	data, // { limit_value: ... }
	token,
	dispatch,
	navigate,
) => {
	const config = {
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
	};

	try {
		const response = await axios.post(
			`${import.meta.env.VITE_API_BACKEND}api/scap/configs/${id}/norway_extension`,
			data,
			config,
		);
		return response.data;
	} catch (error) {
		if (error.response) {
			const status = error.response.status;

			if (status === 401) {
				handleUnauthorized(dispatch, navigate);
			} else if (status === 422) {
				return { errors: error.response.data.errors };
			} else if (status === 400) {
				return {
					errors: error.response.data.errors || {
						Error: ["Bad Request - Please check your inputs."],
					},
				};
			}
		}

		console.error("Error creating scap:", error);
		return null;
	}
};
